// KEY-539: a definition's own word for an action point.
//
// The convention this package has always run on (see dialModel.ts's "A segment (the design's 'action
// point')" and InstanceFilesPanel's translation table) is that `segment` is the DOMAIN term and "action
// point" is the UI term. This module is the one place that translation is made configurable: a definition
// may declare its own pair of words, and every rendered string reads them from here. Nothing else changes —
// no key, enum, route, column or JSON property means anything different.
//
// Lives in @keydials/dial-workspace rather than @keydials/ui deliberately: the reader's input is an
// `InstanceDetail`, so it needs @keydials/api-client (which `ui` does not depend on), and it needs a test
// runner (`ui` has none). This package has vitest and is a dependency of BOTH frontends, which is the
// reach the vocabulary needs.
//
// NOT on DialWorkspaceContext: that context's value is memoised on the assumption hosts pass module-level
// constants, and a vocabulary is per-instance.

import type { GeneratedSection, GeneratedSegment, InstanceDetail } from "@keydials/api-client";

/**
 * The four forms a rendered string can need. Two independent words, each with a sentence-initial variant.
 *
 * Why the plural is stored rather than derived: no rule survives contact with a real vocabulary. "criterion"
 * → "criteria", "matrix" → "matrices". A single site in this package used to append an "s" and it is now a
 * `one`/`many` pick; nothing here may reintroduce one.
 */
export interface SegmentNoun {
  /** Lower case, singular — mid-sentence: "this action point". */
  one: string;
  /** Lower case, plural — mid-sentence: "all action points". */
  many: string;
  /** Sentence-initial singular: "Action point". */
  One: string;
  /** Sentence-initial plural: "Action points". */
  Many: string;
}

/**
 * The platform's own word. Byte-identical to the literals every surface used before this existed, so a
 * definition that declares nothing renders exactly as it always did — which is what the unchanged existing
 * test files assert.
 */
export const DEFAULT_SEGMENT_NOUN: SegmentNoun = {
  one: "action point",
  many: "action points",
  One: "Action point",
  Many: "Action points",
};

/**
 * Capitalises the first CODE POINT only, leaving the rest alone.
 *
 * Not a title-caser: an author may legitimately write "key segment", which has to become "Key segment" and
 * not "Key Segment". Splitting on code points rather than UTF-16 units keeps a word opening with an
 * astral character intact.
 *
 * Note there is deliberately no `toUpperCase()` anywhere in this package: the one chip that renders in caps
 * (`ScopePill`) gets them from CSS `text-transform`, which handles any word in any script without this
 * module having to know the locale.
 */
function sentenceCase(word: string): string {
  const [first, ...rest] = [...word];
  return first === undefined ? word : first.toLocaleUpperCase() + rest.join("");
}

/**
 * "a" or "an" for a supplied word — "an action point", "a segment".
 *
 * Needed because the platform's own word begins with a vowel and the first word a partner asked for does
 * not, so every string that read "an action point" would otherwise render "an segment". A vowel-letter test
 * rather than a pronunciation one: it is right for the short, plain nouns this field accepts, and the
 * alternative is a pronunciation dictionary for a 40-character text box.
 */
export function article(word: string): "a" | "an" {
  return /^[aeiou]/i.test(word.trim()) ? "an" : "a";
}

/**
 * The words to render this dial's action points with.
 *
 * Falls back to {@link DEFAULT_SEGMENT_NOUN} whenever the definition has declared nothing — absent detail,
 * null fields, blank strings, or only one of the two supplied. The half state should never reach a client
 * (the domain clears both forms together and the write endpoint 400s on exactly one), but it is handled
 * here too so no caller has to: a dial showing "segment" beside "action points" would read worse than one
 * showing neither.
 */
export function segmentNoun(
  detail?:
    | (Pick<InstanceDetail, "segmentNounSingular" | "segmentNounPlural"> & {
        sections?: readonly SectionNounSource[] | null;
      })
    | null,
  sectionKey?: string | null,
): SegmentNoun {
  // KEY-762: a section may name its own lists ("dentist" in Healthcare); where it does, that wins for the section.
  return (
    sectionSegmentNoun(detail, sectionKey)
    ?? nounPair(detail?.segmentNounSingular, detail?.segmentNounPlural)
    ?? DEFAULT_SEGMENT_NOUN
  );
}

/**
 * KEY-762: the section's OWN word for its lists, or null when it doesn't name one (so the caller can tell "this
 * section says dentist" from "this dial says action point"). Most callers want {@link segmentNoun} instead.
 */
export function sectionSegmentNoun(
  detail: { sections?: readonly SectionNounSource[] | null } | null | undefined,
  sectionKey: string | null | undefined,
): SegmentNoun | null {
  if (sectionKey == null) {
    return null;
  }
  const section = detail?.sections?.find((s) => s.key === sectionKey);
  return nounPair(section?.segmentNounSingular, section?.segmentNounPlural);
}

type SectionNounSource = Pick<GeneratedSection, "key" | "segmentNounSingular" | "segmentNounPlural">;

/** Both forms, trimmed and sentence-cased — or null when either is missing, so a half pair is never shown. */
function nounPair(singular: string | null | undefined, plural: string | null | undefined): SegmentNoun | null {
  const one = singular?.trim();
  const many = plural?.trim();
  return one && many ? { one, many, One: sentenceCase(one), Many: sentenceCase(many) } : null;
}

/**
 * KEY-762: whether a section's item cards are a single pane (no Files, Discussion or separate Details tab). Looks the
 * section up on anything carrying the dial's `sections`; false for an unknown section or one that doesn't say.
 * KEY-853: given a `segmentKey`, that action point's own setting wins — true Simple, false Tabbed — and a segment
 * that says nothing (null/absent, or not found) follows its section.
 */
export function simpleItemCards(
  detail:
    | {
        sections?:
          | readonly (Pick<GeneratedSection, "key" | "simpleItemCards"> & {
              segments?: readonly Pick<GeneratedSegment, "key" | "simpleItemCards">[] | null;
            })[]
          | null;
      }
    | null
    | undefined,
  sectionKey: string | null | undefined,
  segmentKey?: string | null,
): boolean {
  if (sectionKey == null) {
    return false;
  }
  const section = detail?.sections?.find((s) => s.key === sectionKey);
  const own = segmentKey == null ? null : section?.segments?.find((sg) => sg.key === segmentKey)?.simpleItemCards;
  return own ?? section?.simpleItemCards === true;
}

// ---------------------------------------------------------------------------------------------------------------
// KEY-753: a section's own word for its items.
// ---------------------------------------------------------------------------------------------------------------

/** The four forms of an item noun — the same shape as {@link SegmentNoun}. */
export type ItemNoun = SegmentNoun;

/** The platform's own word for an item. Byte-identical to the literals the item copy used before KEY-753. */
export const DEFAULT_ITEM_NOUN: ItemNoun = {
  one: "item",
  many: "items",
  One: "Item",
  Many: "Items",
};

type ItemNounSource = Pick<GeneratedSection, "key" | "itemNounSingular" | "itemNounPlural">;

/**
 * The words for one section's items (KEY-753) — "appointment"/"appointments" on a family dial's Appointments
 * section, "item"/"items" everywhere a section declares nothing.
 *
 * `source` is the section itself, or anything carrying the dial's `sections` (an `InstanceDetail`) together with
 * the `sectionKey` to look up. Falls back to {@link DEFAULT_ITEM_NOUN} exactly as {@link segmentNoun} falls back:
 * absent source, unknown section, null or blank fields, or only one of the pair supplied.
 */
export function itemNouns(
  source: ItemNounSource | { sections?: readonly ItemNounSource[] | null } | null | undefined,
  sectionKey?: string | null,
): ItemNoun {
  const section =
    source && "sections" in source
      ? sectionKey == null
        ? undefined
        : source.sections?.find((s) => s.key === sectionKey)
      : (source as ItemNounSource | null | undefined);
  const one = section?.itemNounSingular?.trim();
  const many = section?.itemNounPlural?.trim();
  if (!one || !many) {
    return DEFAULT_ITEM_NOUN;
  }

  return { one, many, One: sentenceCase(one), Many: sentenceCase(many) };
}

/**
 * One form of a section's item noun, as a string — `itemNoun(detail, "appts")` is "appointment",
 * `itemNoun(detail, "appts", { plural: true, capitalised: true })` is "Appointments". See {@link itemNouns}.
 */
export function itemNoun(
  source: Parameters<typeof itemNouns>[0],
  sectionKey?: string | null,
  options: { plural?: boolean; capitalised?: boolean } = {},
): string {
  const noun = itemNouns(source, sectionKey);
  if (options.plural) {
    return options.capitalised ? noun.Many : noun.many;
  }
  return options.capitalised ? noun.One : noun.one;
}

/**
 * What the dial's own section/action-point list tab is called (KEY-646): "Subdials" on a group, where the
 * slot lists the child dials instead (KEY-529), and "Sections" everywhere else.
 *
 * Not renamable through {@link dialLabel} (KEY-670): what this slot lists is already worded by the group flag and,
 * for the action points inside it, by KEY-539's noun.
 */
export function listTabLabel(detail: Pick<InstanceDetail, "isGroup">): string {
  return detail.isGroup ? "Subdials" : "Sections";
}

// ---------------------------------------------------------------------------------------------------------------
// KEY-670: a definition's own names for the dial's fixed tab headings.
// ---------------------------------------------------------------------------------------------------------------

/** The levels a tab can sit at, in the code's own terms — these are keys, not display text. */
export type DialLevel = "dial" | "section" | "segment" | "item";

/**
 * Every renamable tab and the levels it appears on. MIRRORS the server's `DialLabelKeys` (KeyDials.Domain) — the
 * server refuses any key not in its list, so a key added here alone can never be set, and one added there alone
 * is stored and rendered by nothing. Both sides pin the flattened list in a test so drift fails loudly.
 */
const DIAL_LABEL_TABS = [
  ["overview", ["dial", "section", "segment"]],
  ["about", []],
  ["guidance", ["dial", "section", "segment", "item"]],
  ["info", []],
  ["items", []],
  ["progress", []],
  ["details", []],
  ["discussion", ["dial", "section", "segment", "item"]],
  ["files", ["dial", "section", "segment", "item"]],
  ["activity", ["dial", "section", "segment"]],
] as const satisfies readonly (readonly [string, readonly DialLevel[]])[];

/** A renamable tab, level-agnostic. */
export type DialLabelTab = (typeof DIAL_LABEL_TABS)[number][0];

type LevelKeys<T> = T extends readonly [infer Tab extends string, infer Levels extends readonly DialLevel[]]
  ? Tab | `${Tab}.${Levels[number]}`
  : never;

/** Any accepted key: a tab's bare key (every level) or `tab.level` (that level only). */
export type DialLabelKey = LevelKeys<(typeof DIAL_LABEL_TABS)[number]>;

/** The platform's wording for each tab — byte-identical to the literals the strips used before KEY-670, so a dial
 *  that renames nothing renders exactly as it always did. */
export const DEFAULT_TAB_LABELS: Record<DialLabelTab, string> = {
  overview: "Overview",
  about: "About",
  guidance: "Guidance",
  info: "Key Info",
  items: "Items",
  progress: "Progress",
  details: "Details",
  discussion: "Discussion",
  files: "Files",
  activity: "Activity",
};

/** Every accepted key, flattened in the server's order: each tab's bare key, then its per-level keys. */
export const DIAL_LABEL_KEYS: readonly DialLabelKey[] = DIAL_LABEL_TABS.flatMap(
  ([tab, levels]) => [tab, ...levels.map((level) => `${tab}.${level}`)] as DialLabelKey[],
);

/** The tabs a definition can rename, with their levels, for the admin editor's grid. */
export const DIAL_LABEL_TAB_LEVELS: readonly { tab: DialLabelTab; levels: readonly DialLevel[] }[] =
  DIAL_LABEL_TABS.map(([tab, levels]) => ({ tab, levels }));

/** Longest override the server accepts, after trimming (`DialLabelKeys.MaxLength`). */
export const DIAL_LABEL_MAX_LENGTH = 40;

/**
 * What this dial calls one of its tabs (KEY-670) — e.g. `dialLabel(detail, "guidance.segment")` is "Deliverables"
 * on the Wipro tracker and "Guidance" everywhere else.
 *
 * Resolution, first non-blank wins: the exact key (`guidance.segment`), then the tab's bare key (`guidance`, which
 * renames it at every level), then the platform's wording. Trimmed, so a stray space never reaches a tab strip.
 * Absent detail, a null map or an unknown key all fall through to the default — a display preference must never be
 * the reason a tab has no heading.
 *
 * Counts and other suffixes (" · 3 ▶") are appended by the caller: this returns the WORD only.
 */
export function dialLabel(
  detail: Pick<InstanceDetail, "labelOverrides"> | null | undefined,
  key: DialLabelKey,
): string {
  const tab = key.split(".")[0] as DialLabelTab;
  const overrides = detail?.labelOverrides;
  const own = overrides?.[key]?.trim();
  if (own) {
    return own;
  }

  const every = key === tab ? undefined : overrides?.[tab]?.trim();
  return every || DEFAULT_TAB_LABELS[tab];
}
